import { Question } from '../types'
import { CheckCircle, XCircle, Lightbulb, BookOpen } from 'lucide-react'

interface QuizCardProps {
  question: Question
  selectedAnswer?: string
  onAnswerSelect: (questionId: string, answer: string) => void
  showResults?: boolean
  /** Show answer immediately after selection (one-by-one mode) */
  immediateReveal?: boolean
  /** When false, allow changing a previously-selected answer (navigation back) */
  locked?: boolean
}

const OPTION_LETTERS = ['A', 'B', 'C', 'D']

export default function QuizCard({
  question,
  selectedAnswer,
  onAnswerSelect,
  showResults = false,
  immediateReveal = false,
  locked = true,
}: QuizCardProps) {
  const answered = !!selectedAnswer
  const isCorrect = selectedAnswer === question.correctAnswer
  const revealNow = showResults || (immediateReveal && answered && locked)

  return (
    <div className="glass-card p-6">
      {/* Question */}
      <h3 className="text-base font-semibold mb-5 leading-relaxed" style={{ color: '#F1F5F9' }}>
        {question.question}
      </h3>

      {/* Options */}
      <div className="space-y-3">
        {question.options.map((option, index) => {
          const letter = OPTION_LETTERS[index] ?? String(index + 1)
          const isSelected = selectedAnswer === option
          const isCorrectOpt = option === question.correctAnswer
          const showGreen = revealNow && isCorrectOpt
          const showRed = revealNow && isSelected && !isCorrect

          let borderColor = 'rgba(255,255,255,0.08)'
          let bgColor = 'transparent'
          if (!revealNow && isSelected) {
            borderColor = '#4F46E5'
            bgColor = 'rgba(79,70,229,0.08)'
          } else if (showGreen) {
            borderColor = '#10B981'
            bgColor = 'rgba(16,185,129,0.08)'
          } else if (showRed) {
            borderColor = '#EF4444'
            bgColor = 'rgba(239,68,68,0.08)'
          }

          return (
            <button
              key={index}
              onClick={() => !revealNow && onAnswerSelect(question.id, option)}
              disabled={revealNow}
              className="w-full text-left flex items-center gap-3 px-4 py-3 rounded-xl transition-all"
              style={{
                border: `1px solid ${borderColor}`,
                background: bgColor,
                cursor: revealNow ? 'default' : 'pointer',
              }}
              onMouseEnter={(e) => {
                if (!revealNow && !isSelected) e.currentTarget.style.borderColor = 'rgba(79,70,229,0.4)'
              }}
              onMouseLeave={(e) => {
                if (!revealNow && !isSelected) e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'
              }}
            >
              {/* Letter badge */}
              <span
                className="w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{
                  background: showGreen
                    ? 'rgba(16,185,129,0.2)'
                    : showRed
                    ? 'rgba(239,68,68,0.2)'
                    : isSelected && !revealNow
                    ? 'rgba(79,70,229,0.2)'
                    : 'rgba(255,255,255,0.06)',
                  color: showGreen ? '#10B981' : showRed ? '#EF4444' : isSelected && !revealNow ? '#818CF8' : '#64748B',
                }}
              >
                {letter}
              </span>
              <span
                className="flex-1 text-sm"
                style={{
                  color: showGreen ? '#10B981' : showRed ? '#EF4444' : isSelected && !revealNow ? '#E2E8F0' : '#CBD5E1',
                }}
              >
                {option}
              </span>
              {showGreen && (
                <span className="flex items-center gap-1.5 flex-shrink-0">
                  <span className="text-xs font-bold uppercase tracking-wide" style={{ color: '#10B981' }}>
                    Correcto
                  </span>
                  <CheckCircle className="w-4 h-4" style={{ color: '#10B981' }} />
                </span>
              )}
              {showRed && <XCircle className="w-4 h-4 flex-shrink-0" style={{ color: '#EF4444' }} />}
            </button>
          )
        })}
      </div>

      {/* Explanation */}
      {revealNow && question.explanation && (
        <div
          className="mt-5 rounded-xl p-4"
          style={{ background: 'rgba(251,191,36,0.06)', border: '1px solid rgba(251,191,36,0.15)' }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 flex-shrink-0" style={{ color: '#FBBF24' }} />
            <span className="text-xs font-semibold" style={{ color: '#FBBF24' }}>Explicación</span>
          </div>
          <p className="text-sm leading-relaxed" style={{ color: '#CBD5E1' }}>{question.explanation}</p>
          {question.sourceReference && (
            <div
              className="mt-3 flex items-start gap-2 pl-3 rounded-r-lg py-2"
              style={{ borderLeft: '2px solid rgba(251,191,36,0.3)', background: 'rgba(251,191,36,0.04)' }}
            >
              <BookOpen className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#92400E' }} />
              <p className="text-xs italic leading-relaxed" style={{ color: '#78716C' }}>
                {question.sourceReference}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
